import { Divider } from '@material-ui/core'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { useTaxation } from 'hooks/useTaxation'
import { SUPPLIER_INTEGRATION_TYPES } from 'opensolar-common'
import { BaseTheme, Button, Grid, Notification, styled, TruckOutlineIcon, Typography } from 'opensolar-ui'
import useCalculateShippingCost from 'pages/ordering/checkoutv3/preOrder/hooks/useCalculateShippingCost'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { CartDiscount, getCartDiscount, getCartDiscountValue } from 'pages/ordering/order/CartDiscounts'
import OrderLineItem from 'pages/ordering/OrderLineItem'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { isManualSource } from 'pages/ordering/utils'
import { useTranslate } from 'ra-core'
import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ActionType, EventType } from 'types/tracking'
import { formatCurrency, formatCurrencyWithSymbol } from 'util/misc'
import DealBreakdownAccordion from '../checkoutv3/preOrder/components/DealBreakdownAccordion'
import { useDealTotals } from '../deals/hooks/useDealTotals'
import { ConsolidatedLineItem, useCartData } from './useCartData'

const SectionCardTop = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutralGrey[100],
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottom: `1px solid ${theme.palette.neutralGrey[500]}`,
  padding: '16px 24px',
}))

const SectionCardBottom = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  padding: '24px',
}))

const TitleContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const SectionHeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.neutralGrey[1300],
}))

const MainContentContainer = styled('div')(({ theme }) => ({
  width: '100%',
  paddingBottom: '8px',
  borderBottom: `1px solid ${theme.palette.neutralGrey[800]}`,
}))

const SupplierSection = styled('div')({
  marginBottom: '16px',
})

const SummaryRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '8px 0px 8px 0px',
})

const SupplierName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'Inter',
  lineHeight: '20px',
})

const SubtotalLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  fontFamily: 'Inter',
  lineHeight: '20px',
  fontStyle: 'normal',
})

const TotalRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '18px',
  paddingTop: '8px',
})

const TotalLabel = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  color: theme.palette.neutralGrey[1300],
  fontWeight: 600,
}))

const TaxLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.neutralGrey[1300],
  fontWeight: 400,
  lineHeight: '20px',
  fontStyle: 'inter',
  paragraphSpacing: '16px',
}))

const TaxRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '8px',
})

const TaxValuePlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 200,
  color: theme.palette.neutralGrey[1000],
}))

const ActionContainer = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
  gap: '12px',
  marginTop: '8px',
})

type SupplierBasket = {
  supplier: HardwareSupplierFilterKeyType
  items: ConsolidatedLineItem[]
  subtotal: number
  itemCount: number
}

type OrderSummaryPanelProps = {
  lineItems: ConsolidatedLineItem[]
  onProceedToCheckout: () => void
  disabled: boolean
}

const OrderSummaryPanel = memo(({ lineItems, onProceedToCheckout, disabled }: OrderSummaryPanelProps) => {
  const translate = useTranslate()
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)
  const orgCountry = useSelector(authSelectors.getCurrentOrgCountry) || ''
  const { taxRate, taxName } = useTaxation(orgCountry)
  const { getSupplierConfig } = useGetSupplierConfig()
  const { shippingCost } = useCalculateShippingCost(undefined)
  const { trackEvent } = useTrackComponent({
    componentKey: 'shop_cart_page',
    eventName: 'Hardware Cart',
  })

  const { discountTotal: dealDiscount, freeShipping: dealFreeShipping, dealBreakdown } = useDealTotals(lineItems)

  const [cartDiscount, setCartDiscount] = useState<CartDiscount>(null)
  const [cartDiscountValue, setCartDiscountValue] = useState(0)

  const supplierBaskets: SupplierBasket[] = []
  const basketMap = new Map<HardwareSupplierFilterKeyType, SupplierBasket>()

  lineItems.forEach((lineItem) => {
    if (lineItem.hasMixedSources) return

    const supplier = lineItem.selectedDistributor as HardwareSupplierFilterKeyType
    if (!supplier || isManualSource(supplier)) return

    if (!basketMap.has(supplier)) {
      basketMap.set(supplier, {
        supplier,
        items: [],
        subtotal: 0,
        itemCount: 0,
      })
    }

    const basket = basketMap.get(supplier)!
    basket.items.push(lineItem)
    basket.itemCount += lineItem.quantity

    const orderingData = lineItem.orderingData
    if (orderingData && orderingData.length > 0) {
      const distributorData = orderingData.find((d) => d.distributor === supplier)
      if (distributorData) {
        const unitPrice = distributorData?.price ?? distributorData?.price_detail?.price_ex_vat ?? 0
        basket.subtotal += unitPrice * lineItem.quantity
      }
    }
  })

  supplierBaskets.push(...Array.from(basketMap.values()))

  const hasPoaItems = useMemo(() => {
    return supplierBaskets.some((basket) => {
      const integrationType = getSupplierConfig(basket.supplier)?.integrationType
      if (integrationType === SUPPLIER_INTEGRATION_TYPES.PRICE_ON_APPLICATION) return true
      if (integrationType === SUPPLIER_INTEGRATION_TYPES.PRICE_ON_APPLICATION_WITH_TIERS && basket.subtotal === 0)
        return true
      return false
    })
  }, [supplierBaskets, getSupplierConfig])

  const rawTotal = supplierBaskets.reduce((sum, basket) => sum + basket.subtotal, 0)
  const totalBeforeTax = Math.max(rawTotal - dealDiscount, 0)

  useEffect(() => {
    const firstBasket = supplierBaskets[0]
    if (!firstBasket?.items?.length) {
      setCartDiscount(null)
      setCartDiscountValue(0)
      return
    }
    getCartDiscount(
      firstBasket.items as unknown as OrderLineItem[],
      firstBasket.supplier,
      (discount: CartDiscount) => {
        setCartDiscount(discount)
        setCartDiscountValue(getCartDiscountValue(firstBasket.subtotal, discount))
      },
      'project'
    )
  }, [lineItems])

  const { hasMixedSourceItems } = useCartData()

  return (
    <div>
      <SectionCardTop>
        <TitleContainer>
          <div style={{ marginLeft: '-4px' }}>
            <TruckOutlineIcon height="24" width="24" />
          </div>
          <SectionHeaderTitle>{translate('Order Summary')}</SectionHeaderTitle>
        </TitleContainer>
      </SectionCardTop>
      <SectionCardBottom>
        <Grid container style={{ gap: '24px' }}>
          <MainContentContainer>
            {supplierBaskets.map((basket) => {
              const supplierConfig = getSupplierConfig(basket.supplier)
              const shippingFeeToDisplay = shippingCost
              const supplierBreakdown = dealBreakdown.filter((e) => e.supplier === basket.supplier)
              const supplierDiscount = supplierBreakdown.reduce((sum, e) => sum + e.discount, 0)
              return (
                <SupplierSection key={basket.supplier}>
                  <SupplierName>{supplierConfig?.name || basket.supplier}</SupplierName>
                  <SummaryRow>
                    <SubtotalLabel>
                      {translate('Subtotal')} ({basket.itemCount}{' '}
                      {basket.itemCount === 1 ? translate('item') : translate('items')}):
                    </SubtotalLabel>
                    <SubtotalLabel>
                      {formatCurrencyWithSymbol(basket.subtotal, currencySymbol, undefined, 2)}
                    </SubtotalLabel>
                  </SummaryRow>
                  <TaxRow>
                    <Typography textVariant="body1">{translate('Shipping & Handling')}</Typography>
                    <Typography textVariant="body1">
                      {dealFreeShipping.includes(basket.supplier)
                        ? 'Free'
                        : typeof shippingFeeToDisplay === 'number'
                          ? formatCurrency(shippingFeeToDisplay)
                          : shippingFeeToDisplay}
                    </Typography>
                  </TaxRow>
                  {supplierDiscount > 0 && (
                    <DealBreakdownAccordion
                      discount={supplierDiscount}
                      breakdown={supplierBreakdown.length > 0 ? supplierBreakdown : dealBreakdown}
                      currencySymbol={currencySymbol}
                      style={{ marginTop: 8, fontSize: '14px !important' }}
                    />
                  )}
                  <Divider style={{ width: '27%', marginLeft: 'auto', marginTop: '8px' }} />
                </SupplierSection>
              )
            })}
            {Boolean(dealDiscount) && !hasMixedSourceItems && (
              <SummaryRow>
                <SubtotalLabel>{translate('Discount Total')}</SubtotalLabel>
                <SubtotalLabel colorHex={BaseTheme.palette.green[600]}>
                  -{formatCurrencyWithSymbol(dealDiscount, currencySymbol, undefined, 2)}
                </SubtotalLabel>
              </SummaryRow>
            )}
            <TotalRow>
              <TotalLabel>{translate('Subtotal')}:</TotalLabel>
              <TotalLabel>{formatCurrencyWithSymbol(totalBeforeTax, currencySymbol, undefined, 2)}</TotalLabel>
            </TotalRow>

            <TaxRow>
              <TaxLabel>
                {taxRate ? `${taxName ? translate(taxName) : translate('Tax')} (${taxRate * 100}%)` : translate('Tax')}:
              </TaxLabel>
              {taxRate ? (
                <TaxLabel>
                  {currencySymbol} {formatCurrency(totalBeforeTax * taxRate)}
                </TaxLabel>
              ) : (
                <TaxValuePlaceholder>{translate('Tax calculated on next step')}</TaxValuePlaceholder>
              )}
            </TaxRow>
            {cartDiscount && cartDiscount.eligible && (
              <TaxRow>
                <TaxLabel>{translate(cartDiscount.discountName)}</TaxLabel>
                <TaxLabel>
                  - {currencySymbol}
                  {formatCurrency(cartDiscountValue)}
                </TaxLabel>
              </TaxRow>
            )}
          </MainContentContainer>

          {hasPoaItems && (
            <Notification
              severity="warning"
              title={translate('Items without pricing')}
              message={translate(
                'This cart contains items without pricing. Confirm pricing via Supplier Assist, or select an alternative source for these items to continue.'
              )}
            />
          )}
          <ActionContainer item xs={12} md={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                trackEvent(
                  EventType.USER_INTERACTION,
                  { type: ActionType.CLICK },
                  { source: 'checkout_button', codes: supplierBaskets.flatMap((b) => b.items.map((item) => item.code)) }
                )
                onProceedToCheckout()
              }}
              disabled={disabled || hasPoaItems}
            >
              {translate('Checkout')}
            </Button>
          </ActionContainer>
        </Grid>
      </SectionCardBottom>
    </div>
  )
})

OrderSummaryPanel.displayName = 'OrderSummaryPanel'
export default OrderSummaryPanel
